// src/pages/admin/billing/AdminBillingServicesPage.js
import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminData } from '../../../contexts/AdminDataContext';
import QuickServiceForm from '../../../components/admin/billing/quote/QuickServiceForm';
import pb from '../../../lib/pocketbase';

function AdminBillingServicesPage() {
  const { loading: adminLoading, services: ctxServices } = useAdminData() || {};
  const [allowEmpty, setAllowEmpty] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [editingService, setEditingService] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [error, setError] = React.useState('');
  const [services, setServices] = React.useState(() => Array.isArray(ctxServices) ? ctxServices.map(s => ({ id: s.id, name: s.name || s.title || 'Service', base_price: Number(s.base_price || s.price || 0), active: s.active !== false })) : []);
  const collectionRef = React.useRef('services_catalog');

  const fetchServices = async () => {
    setError('');
    try {
      const res = await pb.collection(collectionRef.current).getList(1, 200, { sort: 'name' });
      setServices(res?.items ?? []);
    } catch (e) {
      setError(e?.message || 'Erreur chargement des prestations');
      setServices([]);
    }
  };

  React.useEffect(() => {
    fetchServices();
  }, []);

  // Seed from global cache instantly on context changes
  React.useEffect(() => {
    if (Array.isArray(ctxServices) && ctxServices.length) {
      setServices(ctxServices.map(s => ({ id: s.id, name: s.name || s.title || 'Service', base_price: Number(s.base_price || s.price || 0), active: s.active !== false })));
    }
  }, [ctxServices]);

  // Prevent empty-state flash on first refresh: allow empty only after admin finished loading
  React.useEffect(() => {
    if (adminLoading) { setAllowEmpty(false); return; }
    const t = setTimeout(() => setAllowEmpty(true), 250);
    return () => { clearTimeout(t); };
  }, [adminLoading]);

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (editingService?.id) {
        await pb.collection(collectionRef.current).update(editingService.id, data);
      } else {
        await pb.collection(collectionRef.current).create(data);
      }
      await fetchServices();
      setOpen(false);
      setEditingService(null);
    } catch (e) {
      alert(e?.message || "Erreur d'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette prestation ?')) return;
    try {
      await pb.collection(collectionRef.current).delete(id);
      await fetchServices();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la suppression');
    }
  };

  return (
    <AdminLayout title="">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap:'wrap' }}>
        <h1 style={{ marginTop: 0 }}>Prestations</h1>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:'auto' }}>
          <button
            className="btn-primary"
            onClick={() => { setEditingService(null); setOpen(true); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 10,
              background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
              border: '1px solid rgba(79,70,229,0.6)',
              boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
              color: 'white'
            }}
          >
            <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:24, height:24, borderRadius:999, background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.25)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </span>
            <span>Créer une prestation</span>
          </button>
        </div>
      </div>
      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .header-pill{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:8px 10px; border-radius:10px; }
        .header-pill:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .customer-avatar { width:36px; height:36px; border-radius:999px; display:grid; place-items:center; font-weight:700; background:#3b82f6; color:#fff; }
        .header-shape{ border-radius:10px; padding:8px 10px; }
        .searchbar{ position:relative; display:flex; align-items:center; gap:8px; }
        .searchbar .icon{ position:absolute; left:10px; color: var(--admin-muted); }
        .searchbar input{ width:260px; background: var(--admin-panel); border:1px solid var(--admin-border); color: var(--admin-text); border-radius:10px; padding:8px 10px 8px 30px; outline:none; transition: box-shadow .15s ease, border-color .15s ease; }
        .searchbar input::placeholder{ color: var(--admin-muted); }
        .searchbar input:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        .header-actions-frame{ display:flex; gap:8px; padding:0; border:none; background: transparent; }
        .header-actions-frame .header-pill{ border:1px solid var(--admin-border); background: var(--admin-panel-2); }
        .admin-shell.admin-light .header-actions-frame .header-pill:hover{ background: rgba(2,6,23,0.06); }

        /* Mobile tweaks to prevent overflow */
        @media (max-width: 720px) {
          .services-header{ flex-wrap: wrap; }
          .services-search{ width:100%; margin-left:0 !important; }
          .services-search.searchbar{ width:100%; }
          .services-search.searchbar input{ width:100%; }
        }
        @media (max-width: 480px) {
          .services-grid{ grid-template-columns: 1fr !important; }
        }
      `}</style>
      {error && <div style={{ color:'#ef4444', marginBottom:8 }}>{error}</div>}

      <div className="card" style={{ marginTop: 12 }}>
        <div className="services-header" style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <div className="header-actions-frame">
            <button className="header-pill header-shape has-tip" data-tip="QR Prestations" title="QR Prestations" aria-label="QR Prestations">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z"/>
              </svg>
            </button>
            <button className="header-pill header-shape has-tip" data-tip="Copier le lien" onClick={async () => {
              const link = window.location.origin + '/admin/facturation/prestations';
              try { await navigator.clipboard.writeText(link); alert('Lien copié'); } catch {}
            }}>Copier le lien</button>
            <button className="header-pill header-shape has-tip" data-tip="Partager" onClick={async () => {
              const link = window.location.origin + '/admin/facturation/prestations';
              const shareText = `Accéder à la page Prestations :\n${link}`;
              if (navigator.share) { try { await navigator.share({ title: 'Prestations', text: shareText, url: link }); } catch {} }
              else { try { await navigator.clipboard.writeText(shareText); alert('Texte copié'); } catch { alert('Partage non supporté'); } }
            }}>Partager</button>
          </div>
          <div className="searchbar services-search">
            <svg className="icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
            <input
              type="text"
              placeholder="Rechercher une prestation..."
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Services grid */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="services-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:12 }}>
          {services
            .filter(s =>
              (s.name?.toLowerCase() || '').includes(search.toLowerCase()) ||
              (s.category?.toLowerCase() || '').includes(search.toLowerCase())
            )
            .map((s) => (
              <div key={s.id} style={{ border:'1px solid var(--admin-border)', borderRadius:12, padding:12, background:'linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2))' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div className="customer-avatar">
                    {(s.name || '—').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontWeight:700, overflow:'hidden', textOverflow:'ellipsis' }}>{s.name || '—'}</div>
                    <div style={{ color:'var(--admin-muted)', fontSize:'.9rem' }}>{s.category || '—'}</div>
                  </div>
                  <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ padding:'4px 8px', borderRadius:999, border:'1px solid var(--admin-border)', fontSize:'.8rem' }}>{s.active === false ? 'inactif' : 'actif'}</span>
                    <span style={{ color:'var(--admin-accent-2)', fontWeight:700 }}>{typeof s.base_price === 'number' ? `${s.base_price.toFixed(2)}€` : (s.base_price ? `${Number(s.base_price).toFixed(2)}€` : '—')}</span>
                  </div>
                </div>

                {s.description && (
                  <div style={{ marginTop:10, color:'var(--admin-text)', fontSize:'.95rem' }}>
                    <div style={{ overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{s.description}</div>
                  </div>
                )}

                <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:10 }}>
                  <button className="header-pill" title="Modifier" aria-label="Modifier" onClick={() => { setEditingService(s); setOpen(true); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                  <button className="header-pill" title="Supprimer" aria-label="Supprimer" onClick={() => handleDelete(s.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
                  </button>
                  <div style={{ marginLeft:'auto', color:'var(--admin-muted)', fontSize:'.85rem' }}>Maj le {new Date(s.updated || s.created).toLocaleDateString()}</div>
                </div>
              </div>
            ))}

          {services.filter(s => (s.name?.toLowerCase() || '').includes(search.toLowerCase()) || (s.category?.toLowerCase() || '').includes(search.toLowerCase())).length === 0 && !adminLoading && allowEmpty && (
            <div style={{ gridColumn:'1 / -1', padding:16, display:'flex', alignItems:'center', justifyContent:'center', minHeight: 200, width:'100%' }}>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ opacity:.7, display:'block' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <h3 style={{ margin:0 }}>Aucune prestation trouvée</h3>
                <p style={{ margin:0, color:'var(--admin-muted)' }}>
                  {search ? `Aucun résultat pour "${search}"` : 'Commencez par créer votre première prestation'}
                </p>
                {!search && (
                  <button
                    className="btn-primary"
                    onClick={() => { setEditingService(null); setOpen(true); }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '10px 14px', borderRadius: 10,
                      background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
                      border: '1px solid rgba(79,70,229,0.6)',
                      boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                      color: 'white'
                    }}
                  >
                    <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22, borderRadius:999, background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.25)' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </span>
                    <span>Nouvelle prestation</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {open && (
        <QuickServiceForm
          onSubmit={handleSubmit}
          onCancel={() => { setOpen(false); setEditingService(null); }}
          loading={loading}
          initialData={editingService}
        />
      )}
    </AdminLayout>
  );
}

export default AdminBillingServicesPage;
